import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { hapticFeedback } from '../../utils/telegram';
import './AdminDashboard.css';
import FormattedNumber from '../common/FormattedNumber/FormattedNumber';

const ADMIN_TABS = {
  REFERRAL_TRANSFER: 'REFERRAL_TRANSFER',
  WITHDRAWAL_MANAGEMENT: 'WITHDRAWAL_MANAGEMENT',
  BONUS_MANAGEMENT: 'BONUS_MANAGEMENT'
};

const AdminDashboard = () => {
  const { user } = useUser();
  const { t } = useLanguage();
  const {
    transferReferral,
    getWithdrawals,
    approveWithdrawal,
    rejectWithdrawal,
    giveBonusMoney,
    giveBonusDeals,
    searchUsers
  } = useApi();

  const [activeTab, setActiveTab] = useState(ADMIN_TABS.REFERRAL_TRANSFER);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notification, setNotification] = useState(null);

  // Referral Transfer State
  const [userUsername, setUserUsername] = useState('');
  const [newReferrerUsername, setNewReferrerUsername] = useState('');
  
  // Search results for referral transfer
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [referrerSearchResults, setReferrerSearchResults] = useState([]);

  // Withdrawal Management State
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false);
  const [confirmPopup, setConfirmPopup] = useState(null);

  // Bonus Management State
  const [bonusUsername, setBonusUsername] = useState('');
  const [bonusAmount, setBonusAmount] = useState('');
  const [bonusDeals, setBonusDeals] = useState('');
  const [bonusType, setBonusType] = useState('money'); // 'money' or 'deals'

  // Search results
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (activeTab === ADMIN_TABS.WITHDRAWAL_MANAGEMENT) {
      loadWithdrawals();
    }
  }, [activeTab]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const loadWithdrawals = async () => {
    setWithdrawalsLoading(true);
    try {
      const data = await getWithdrawals();
      setWithdrawals(data);
    } catch (err) {
      console.error('Error loading withdrawals:', err);
      setError(t('operationFailed'));
    } finally {
      setWithdrawalsLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    hapticFeedback.light();
    setActiveTab(tab);
    clearMessages();
  };

  const handleReferralTransfer = async (e) => {
    e.preventDefault();
    if (!userUsername.trim() || !newReferrerUsername.trim()) {
      setError(t('fillAllFields'));
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      const result = await transferReferral(userUsername.trim(), newReferrerUsername.trim());
      const details = result.details || {};
      setSuccess(t('referralTransferSuccess'));
      showNotification(`✅ ${t('referralTransferSuccess')}`, 'success');
      setUserUsername('');
      setNewReferrerUsername('');
      setUserSearchResults([]);
      setReferrerSearchResults([]);
      hapticFeedback.success();
    } catch (err) {
      console.error('Error transferring referral:', err);
      setError(err.response?.data?.message || t('operationFailed'));
      showNotification(`❌ ${t('operationFailed')}`, 'error');
      hapticFeedback.error();
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalConfirm = (withdrawal) => {
    setConfirmPopup({
      type: 'confirm',
      withdrawal,
      title: t('confirmApproval'),
      message: t('approvalQuestion'),
      confirmText: t('confirm'),
      cancelText: t('cancel')
    });
  };

  const handleWithdrawalReject = (withdrawal) => {
    setConfirmPopup({
      type: 'reject',
      withdrawal,
      title: t('confirmRejection'),
      message: t('rejectionQuestion'),
      confirmText: t('reject'),
      cancelText: t('cancel')
    });
  };

  const handlePopupConfirm = async () => {
    if (!confirmPopup) return;

    const { type, withdrawal } = confirmPopup;
    setLoading(true);
    clearMessages();
    setConfirmPopup(null);

    try {
      if (type === 'confirm') {
        await approveWithdrawal(withdrawal.id);
        setSuccess(t('withdrawalApproved'));
        showNotification(`✅ ${t('withdrawalApproved')}`, 'success');
      } else if (type === 'reject') {
        await rejectWithdrawal(withdrawal.id);
        setSuccess(t('withdrawalRejected'));
        showNotification(`❌ ${t('withdrawalRejected')}`, 'warning');
      }
      
      await loadWithdrawals(); // Refresh the list
      hapticFeedback.success();
    } catch (err) {
      console.error(`Error processing withdrawal:`, err);
      setError(err.response?.data?.message || t('operationFailed'));
      showNotification(`❌ ${t('operationFailed')}`, 'error');
      hapticFeedback.error();
    } finally {
      setLoading(false);
    }
  };

  const handlePopupCancel = () => {
    setConfirmPopup(null);
    hapticFeedback.light();
  };

  const handleBonusGive = async (e) => {
    e.preventDefault();
    if (!bonusUsername.trim()) {
      setError(t('fillAllFields'));
      return;
    }

    if (bonusType === 'money' && (!bonusAmount || parseFloat(bonusAmount) <= 0)) {
      setError(t('fillAllFields'));
      return;
    }

    if (bonusType === 'deals' && (!bonusDeals || parseInt(bonusDeals) <= 0)) {
      setError(t('fillAllFields'));
      return;
    }

    setLoading(true);
    clearMessages();

    try {
      if (bonusType === 'money') {
        await giveBonusMoney(bonusUsername.trim(), parseFloat(bonusAmount));
        setSuccess(t('bonusMoneyGiven'));
        showNotification(`✅ ${t('bonusMoneyGiven')}`, 'success');
        setBonusAmount('');
      } else {
        await giveBonusDeals(bonusUsername.trim(), parseInt(bonusDeals));
        setSuccess(t('bonusDealsGiven'));
        showNotification(`✅ ${t('bonusDealsGiven')}`, 'success');
        setBonusDeals('');
      }
      
      setBonusUsername('');
      setSearchResults([]);
      hapticFeedback.success();
    } catch (err) {
      console.error('Error giving bonus:', err);
      setError(err.response?.data?.message || t('operationFailed'));
      showNotification(`❌ ${t('operationFailed')}`, 'error');
      hapticFeedback.error();
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (username) => {
    if (!username.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const results = await searchUsers(username.trim());
      setSearchResults(results);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    }
  };

  const handleUserSearchForTransfer = async (username) => {
    if (!username.trim()) {
      setUserSearchResults([]);
      return;
    }

    try {
      const results = await searchUsers(username.trim());
      setUserSearchResults(results);
    } catch (err) {
      console.error('Error searching users:', err);
      setUserSearchResults([]);
    }
  };

  const handleReferrerSearch = async (username) => {
    if (!username.trim()) {
      setReferrerSearchResults([]);
      return;
    }

    try {
      const results = await searchUsers(username.trim());
      setReferrerSearchResults(results);
    } catch (err) {
      console.error('Error searching users:', err);
      setReferrerSearchResults([]);
    }
  };

  const handleCopyAddress = async (address) => {
    if (!address || address === 'Не указан') return;

    try {
      await navigator.clipboard.writeText(address);
      setSuccess(t('addressCopied'));
      showNotification(`📋 ${t('addressCopied')}`, 'info');
      hapticFeedback.light();
      
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
      showNotification(`❌ ${t('addressCopyFailed')}`, 'error');
    }
  };

  const renderReferralTransfer = () => (
    <div className="admin-section">
      <h2 className="admin-section-title">{t('referralTransferTitle')}</h2>
      <p className="admin-section-description">
        {t('referralTransferDescription')}
      </p>

      <form onSubmit={handleReferralTransfer} className="admin-form">
        <div className="form-group">
          <label className="form-label">{t('usernameUser')}</label>
          <input
            type="text"
            className="form-input"
            value={userUsername}
            onChange={(e) => {
              setUserUsername(e.target.value);
              handleUserSearchForTransfer(e.target.value);
            }}
            placeholder={t('usernamePlaceholder')}
            disabled={loading}
          />
          <div className="form-hint">
            {t('usernameHint')}
          </div>

          {userSearchResults.length > 0 && (
            <div className="search-results">
              {userSearchResults.map(user => (
                <div
                  key={user.id}
                  className="search-result-item"
                  onClick={() => {
                    setUserUsername(user.username);
                    setUserSearchResults([]);
                    hapticFeedback.light();
                  }}
                >
                  <div className="user-info">
                    <div className="username">@{user.username}</div>
                    <div className="user-name">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Имя не указано'}
                    </div>
                  </div>
                  <div className="user-balance">
                    <FormattedNumber value={user.balance} suffix="USDT" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('newReferrer')}</label>
          <input
            type="text"
            className="form-input"
            value={newReferrerUsername}
            onChange={(e) => {
              setNewReferrerUsername(e.target.value);
              handleReferrerSearch(e.target.value);
            }}
            placeholder={t('newReferrerPlaceholder')}
            disabled={loading}
          />
          <div className="form-hint">
            {t('newReferrerHint')}
          </div>

          {referrerSearchResults.length > 0 && (
            <div className="search-results">
              {referrerSearchResults.map(user => (
                <div
                  key={user.id}
                  className="search-result-item"
                  onClick={() => {
                    setNewReferrerUsername(user.username);
                    setReferrerSearchResults([]);
                    hapticFeedback.light();
                  }}
                >
                  <div className="user-info">
                    <div className="username">@{user.username}</div>
                    <div className="user-name">
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Имя не указано'}
                    </div>
                  </div>
                  <div className="user-balance">
                    <FormattedNumber value={user.balance} suffix="USDT" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          type="submit"
          className="admin-button primary"
          disabled={loading || !userUsername.trim() || !newReferrerUsername.trim()}
        >
          {loading ? t('transferring') : t('transferStructure')}
        </button>
      </form>
    </div>
  );

  const renderWithdrawalManagement = () => (
    <div className="admin-section">
      <h2 className="admin-section-title">
        {t('withdrawalManagementTitle')}
        {withdrawals.length > 0 && (
          <span className="count-badge">{withdrawals.length}</span>
        )}
      </h2>
      <p className="admin-section-description">
        {t('withdrawalManagementDescription')}
      </p>

      <div className="withdrawals-controls">
        <button
          className="admin-button secondary"
          onClick={() => {
            hapticFeedback.light();
            loadWithdrawals();
          }}
          disabled={withdrawalsLoading}
        >
          {withdrawalsLoading ? t('loading') : t('update')}
        </button>
      </div>

      <div className="withdrawals-list">
        {withdrawalsLoading ? (
          <div className="loading-state">{t('loadingWithdrawals')}</div>
        ) : withdrawals.length === 0 ? (
          <div className="empty-state">{t('noActiveWithdrawals')}</div>
        ) : (
          withdrawals
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map(withdrawal => (
              <div key={withdrawal.id} className="withdrawal-card">
                <div className="withdrawal-header">
                  <div className="withdrawal-user">
                    <div className="user-info">
                      <div className="username">@{withdrawal.user.username || 'Без username'}</div>
                      <div className="user-name">
                        {[withdrawal.user.firstName, withdrawal.user.lastName]
                          .filter(Boolean)
                          .join(' ') || 'Имя не указано'}
                      </div>
                    </div>
                  </div>
                  <div className="withdrawal-status">
                    <span className={`status-badge ${withdrawal.status === 'PENDING' ? 'pending' : withdrawal.status === 'CONFIRMED' ? 'confirmed' : 'rejected'}`}>
                      {withdrawal.status === 'PENDING' ? t('pending') :
                        withdrawal.status === 'CONFIRMED' ? t('approved') : t('rejected')}
                    </span>
                  </div>
                </div>

                <div className="withdrawal-details">
                  <div className="detail-row">
                    <span className="detail-label">{t('amount')}:</span>
                    <span className="detail-value">
                      <FormattedNumber value={withdrawal.amount} suffix={withdrawal.currency} />
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">{t('address')}:</span>
                    <span
                      className={`detail-value address ${withdrawal.address && withdrawal.address !== 'Не указан' ? 'clickable' : ''}`}
                      onClick={() => handleCopyAddress(withdrawal.address)}
                      title={withdrawal.address && withdrawal.address !== 'Не указан' ? t('clickToCopy') : ''}
                    >
                      {withdrawal.address || t('notSpecified')}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">{t('date')}:</span>
                    <span className="detail-value">
                      {new Date(withdrawal.createdAt).toLocaleString('ru-RU', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>

                {withdrawal.status === 'PENDING' && (
                  <div className="withdrawal-actions">
                    <button
                      className="admin-button success"
                      onClick={() => handleWithdrawalConfirm(withdrawal)}
                      disabled={loading}
                    >
                      {t('confirm')}
                    </button>
                    <button
                      className="admin-button danger"
                      onClick={() => handleWithdrawalReject(withdrawal)}
                      disabled={loading}
                    >
                      {t('reject')}
                    </button>
                  </div>
                )}
              </div>
            ))
        )}
      </div>
    </div>
  );

  const renderBonusManagement = () => (
    <div className="admin-section">
      <h2 className="admin-section-title">{t('bonusManagementTitle')}</h2>
      <p className="admin-section-description">
        {t('bonusManagementDescription')}
      </p>

      <div className="bonus-type-selector">
        <button
          className={`type-button ${bonusType === 'money' ? 'active' : ''}`}
          onClick={() => {
            setBonusType('money');
            hapticFeedback.light();
          }}
        >
          {t('bonusMoney')}
        </button>
        <button
          className={`type-button ${bonusType === 'deals' ? 'active' : ''}`}
          onClick={() => {
            setBonusType('deals');
            hapticFeedback.light();
          }}
        >
          {t('bonusDeals')}
        </button>
      </div>

      <form onSubmit={handleBonusGive} className="admin-form">
        <div className="form-group">
          <label className="form-label">{t('usernameUser')}</label>
          <input
            type="text"
            className="form-input"
            value={bonusUsername}
            onChange={(e) => {
              setBonusUsername(e.target.value);
              handleUserSearch(e.target.value);
            }}
            placeholder={t('usernamePlaceholder')}
            disabled={loading}
          />

          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map(user => (
                <div
                  key={user.id}
                  className="search-result-item"
                  onClick={() => {
                    setBonusUsername(user.username);
                    setSearchResults([]);
                    hapticFeedback.light();
                  }}
                >
                  <div className="user-info">
                    <div className="username">@{user.username}</div>
                    <div className="user-name">{user.firstName} {user.lastName}</div>
                  </div>
                  <div className="user-balance">
                    <FormattedNumber value={user.balance} suffix="USDT" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {bonusType === 'money' ? (
          <div className="form-group">
            <label className="form-label">{t('bonusAmount')}</label>
            <input
              type="number"
              className="form-input"
              value={bonusAmount}
              onChange={(e) => setBonusAmount(e.target.value)}
              placeholder={t('bonusAmountPlaceholder')}
              min="0.01"
              step="0.01"
              disabled={loading}
            />
          </div>
        ) : (
          <div className="form-group">
            <label className="form-label">{t('bonusDeals')}</label>
            <input
              type="number"
              className="form-input"
              value={bonusDeals}
              onChange={(e) => setBonusDeals(e.target.value)}
              placeholder={t('bonusDealsPlaceholder')}
              min="1"
              step="1"
              disabled={loading}
            />
          </div>
        )}

        <button
          type="submit"
          className="admin-button primary"
          disabled={loading || !bonusUsername.trim() ||
            (bonusType === 'money' && (!bonusAmount || parseFloat(bonusAmount) <= 0)) ||
            (bonusType === 'deals' && (!bonusDeals || parseInt(bonusDeals) <= 0))}
        >
          {loading ? t('giving') : t('giveBonus')}
        </button>
      </form>
    </div>
  );

  const renderCurrentSection = () => {
    switch (activeTab) {
      case ADMIN_TABS.REFERRAL_TRANSFER:
        return renderReferralTransfer();
      case ADMIN_TABS.WITHDRAWAL_MANAGEMENT:
        return renderWithdrawalManagement();
      case ADMIN_TABS.BONUS_MANAGEMENT:
        return renderBonusManagement();
      default:
        return renderReferralTransfer();
    }
  };

  // Check if user has admin rights (you should implement proper admin checking)
  const adminIds = ['569452912', '5982701507'];
  if (!user || !adminIds.includes(user.telegramId)) { // Replace with proper admin check
    return (
      <div className="admin-dashboard">
        <div className="access-denied">
          <h2>{t('accessDeniedTitle')}</h2>
          <p>{t('accessDeniedDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Top Notification */}
      {notification && (
        <div className={`top-notification ${notification.type}`}>
          <div className="notification-content-ts">
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="admin-header">
        <h1 className="admin-title">{t('adminTitle')}</h1>
        <div className="admin-user-info">
          {t('adminInfo')} {user.firstName} {user.lastName}
        </div>
      </div>

      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === ADMIN_TABS.REFERRAL_TRANSFER ? 'active' : ''}`}
          onClick={() => handleTabChange(ADMIN_TABS.REFERRAL_TRANSFER)}
        >
          {t('referralTransfer')}
        </button>
        <button
          className={`admin-tab ${activeTab === ADMIN_TABS.WITHDRAWAL_MANAGEMENT ? 'active' : ''}`}
          onClick={() => handleTabChange(ADMIN_TABS.WITHDRAWAL_MANAGEMENT)}
        >
          {t('withdrawalManagement')}
        </button>
        <button
          className={`admin-tab ${activeTab === ADMIN_TABS.BONUS_MANAGEMENT ? 'active' : ''}`}
          onClick={() => handleTabChange(ADMIN_TABS.BONUS_MANAGEMENT)}
        >
          {t('bonusManagement')}
        </button>
      </div>

      {error && (
        <div className="admin-message error">
          {error}
        </div>
      )}

      {success && (
        <div className="admin-message success">
          {success}
        </div>
      )}

      {renderCurrentSection()}

      {/* Confirmation Popup */}
      {confirmPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3 className="popup-title">{confirmPopup.title}</h3>
            <p className="popup-message">{confirmPopup.message}</p>
            <div className="popup-actions">
              <button
                className={`admin-button ${confirmPopup.type === 'confirm' ? 'success' : 'danger'}`}
                onClick={handlePopupConfirm}
                disabled={loading}
              >
                {loading ? t('processing') : confirmPopup.confirmText}
              </button>
              <button
                className="admin-button secondary"
                onClick={handlePopupCancel}
                disabled={loading}
              >
                {confirmPopup.cancelText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 